import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, UserPlus, Search, X } from "lucide-react";
import { fmtDateShort } from "@/lib/format";
import { AssignTurnoDialog } from "@/components/mansioni/AssignTurnoDialog";

const DAY = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const KIND_CLS = { turno: "bg-emerald-50 text-emerald-800 border-emerald-200", impegno: "bg-indigo-50 text-indigo-800 border-indigo-200", permesso: "bg-amber-50 text-amber-800 border-amber-200" };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export default function StudentiLiberi() {
  const qc = useQueryClient();
  const [week, setWeek] = useState(monday(new Date()));
  const [day, setDay] = useState(iso(new Date()));
  const [win, setWin] = useState({ start_time: "", end_time: "" });
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const useWin = win.start_time && win.end_time;

  const { data, isLoading } = useQuery({
    queryKey: ["free-students", iso(week), useWin ? win : null],
    queryFn: async () => (await api.get("/mansioni-area/planner/free", {
      params: { since: iso(week), until: iso(addDays(week, 6)), ...(useWin ? win : {}) },
    })).data,
  });
  const days = data?.days || [];
  const cur = days.find((d) => d.date === day) || days[0];
  const filt = (list) => list.filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()));
  const free = useMemo(() => (cur ? filt(cur.free) : []), [cur, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const busy = useMemo(() => (cur ? filt(cur.busy) : []), [cur, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftWeek = (n) => { const w = addDays(week, 7 * n); setWeek(w); setDay(iso(w)); };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="free-students-page">
      <PageHeader title="Studenti liberi" subtitle="Chi non ha turni, impegni esterni o permessi approvati: assegnalo al volo a una mansione non prevista."/>

      <div className="flex items-center gap-2 mb-3">
        <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} data-testid="free-prev-week"><ChevronLeft size={16}/></Button>
        <div className="text-sm font-medium min-w-[170px] text-center">{fmtDateShort(iso(week))} – {fmtDateShort(iso(addDays(week, 6)))}</div>
        <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} data-testid="free-next-week"><ChevronRight size={16}/></Button>
        <Button variant="ghost" size="sm" onClick={() => { setWeek(monday(new Date())); setDay(iso(new Date())); }} data-testid="free-today">Oggi</Button>
      </div>

      <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-5" data-testid="free-day-strip">
        {days.map((d, i) => (
          <button key={d.date} onClick={() => setDay(d.date)} data-testid={`free-day-${d.date}`}
            className={`rounded-xl border px-1 py-2 sm:py-3 text-center transition-colors duration-150 ${cur?.date === d.date ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 hover:border-[#1C352D]"}`}>
            <div className="text-[10px] uppercase tracking-widest opacity-70">{DAY[i]} {d.date.slice(8)}</div>
            <div className="text-lg sm:text-2xl font-semibold leading-tight">{d.free.length}</div>
            <div className="text-[9px] sm:text-[10px] opacity-70">liberi</div>
          </button>
        ))}
      </div>

      <Card className="p-4 border border-stone-200 rounded-xl shadow-none mb-5 flex flex-wrap items-end gap-3">
        <div><Label className="text-[10px] uppercase tracking-widest">Dalle</Label><Input type="time" value={win.start_time} onChange={(e) => setWin({ ...win, start_time: e.target.value })} className="mt-1 w-32" data-testid="free-start"/></div>
        <div><Label className="text-[10px] uppercase tracking-widest">Alle</Label><Input type="time" value={win.end_time} onChange={(e) => setWin({ ...win, end_time: e.target.value })} className="mt-1 w-32" data-testid="free-end"/></div>
        {(win.start_time || win.end_time) && <Button variant="ghost" size="sm" onClick={() => setWin({ start_time: "", end_time: "" })} data-testid="free-clear-time"><X size={13} className="mr-1"/> Tutto il giorno</Button>}
        <div className="flex-1 min-w-[180px] relative">
          <Label className="text-[10px] uppercase tracking-widest">Cerca</Label>
          <Search size={13} className="absolute left-2 bottom-3 text-stone-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome studente…" className="mt-1 pl-7" data-testid="free-search"/>
        </div>
        <div className="text-[11px] text-stone-500 w-full">
          {useWin ? <>Liberi dalle <b>{win.start_time}</b> alle <b>{win.end_time}</b>.</> : <>Liberi per <b>tutto il giorno</b>. Indica una fascia oraria per vedere anche chi è libero solo in quell'orario.</>}
        </div>
      </Card>

      {isLoading ? <div className="text-sm text-stone-400 p-10 text-center">Caricamento…</div> : cur && (
        <div className="grid lg:grid-cols-2 gap-5">
          <FreeList list={free} date={cur.date} onAssign={(s) => setTarget({ student: s, date: cur.date, ...(useWin ? win : {}) })}/>
          <BusyList list={busy}/>
        </div>
      )}

      <AssignTurnoDialog target={target} onClose={() => setTarget(null)} onSaved={() => qc.invalidateQueries({ queryKey: ["free-students"] })}/>
    </div>
  );
}

function FreeList({ list, date, onAssign }) {
  return (
    <Card className="border border-emerald-200 rounded-xl shadow-none" data-testid="free-list">
      <div className="p-4 border-b border-emerald-100 text-sm font-medium">Liberi il {fmtDateShort(date)} <span className="text-stone-400 font-normal">· {list.length}</span></div>
      {list.length === 0 ? <div className="p-8 text-center text-sm text-stone-400">Nessuno studente libero.</div> : (
        <ul className="divide-y divide-stone-100">
          {list.map((s) => (
            <li key={s.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`free-student-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#1C352D] truncate">{s.name}</div>
                <div className="text-[11px] text-stone-500">{s.year_level ? `${s.year_level}° anno` : "—"}{s.matricola ? ` · ${s.matricola}` : ""}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => onAssign(s)} className="rounded-full border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white" data-testid={`free-assign-${s.id}`}>
                <UserPlus size={13} className="mr-1"/> Assegna
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function BusyList({ list }) {
  return (
    <Card className="border border-stone-200 rounded-xl shadow-none" data-testid="busy-list">
      <div className="p-4 border-b border-stone-100 text-sm font-medium">Occupati <span className="text-stone-400 font-normal">· {list.length}</span></div>
      {list.length === 0 ? <div className="p-8 text-center text-sm text-stone-400">Nessuno occupato.</div> : (
        <ul className="divide-y divide-stone-100">
          {list.map((s) => (
            <li key={s.id} className="px-4 py-2.5" data-testid={`busy-student-${s.id}`}>
              <div className="font-medium text-stone-700 text-sm">{s.name}</div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {s.items.map((it, i) => (
                  <span key={i} className={`text-[10px] rounded-full border px-2 py-0.5 ${KIND_CLS[it.kind]}`}>
                    {it.title}{it.start ? ` · ${it.start}${it.end ? `–${it.end}` : ""}` : " · tutto il giorno"}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
